import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import { getDashboardData } from '../services/api';
import '../styles/dashboard.css';

function Dashboard({ onClose, originRoute }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const svgRef1 = useRef();
  const svgRef2 = useRef();

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('🔄 Loading dashboard data...');
      const data = await getDashboardData();
      console.log('✅ Dashboard data loaded:', data);
      
      if (!data.users || !Array.isArray(data.users)) {
        throw new Error('Invalid response format: expected users array');
      }
      
      setAllUsers(data.users);
      setFilteredUsers(data.users);
    } catch (err) {
      console.error('❌ Dashboard error:', err);
      let errorMsg = err.message || 'Failed to load dashboard data';
      
      // Check for common error patterns
      if (errorMsg.includes('403')) {
        errorMsg = 'Access denied: Your email is not authorized to view the dashboard';
      } else if (errorMsg.includes('401')) {
        errorMsg = 'Authentication failed: Please log in again';
      } else if (errorMsg.includes('404')) {
        errorMsg = 'Dashboard endpoint not found. Ensure backend is running.';
      } else if (errorMsg.includes('Non-JSON response')) {
        errorMsg = 'Server error: ' + errorMsg.substring(0, 150);
      }
      
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filterText.trim() === '') {
        setFilteredUsers(allUsers);
      } else {
        const searchLower = filterText.toLowerCase();
        const filtered = allUsers.filter(user =>
          user.name.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower)
        );
        setFilteredUsers(filtered);
      }
      setCurrentPage(1); // Reset to first page
    }, 300);

    return () => clearTimeout(timer);
  }, [filterText, allUsers]);

  // Draw spider charts when data changes
  useEffect(() => {
    if (filteredUsers.length > 0) {
      const completedUsers = filteredUsers.filter(user => user.completedAt);
      if (completedUsers.length > 0) {
        drawSelfChart(completedUsers);
        drawPeerChart(completedUsers);
      }
    }
  }, [filteredUsers]);

  // Calculate self view averages (only for completed assessments)
  const calculateSelfAverages = (users) => {
    if (users.length === 0) return {};
    const forces = ['spark', 'anchor', 'connector', 'decider', 'builder', 'pulse'];
    const averages = {};
    forces.forEach(force => {
      const sum = users.reduce((acc, user) => acc + (user[force] || 0), 0);
      averages[force] = Math.round(sum / users.length * 10) / 10;
    });
    return averages;
  };

  // Draw self view spider chart
  const drawSelfChart = (usersForChart) => {
    if (!svgRef1.current || usersForChart.length === 0) return;

    const selfAverages = calculateSelfAverages(usersForChart);
    drawSpiderChart(svgRef1.current, selfAverages);
  };

  // Draw peer view spider chart (from peer assessments)
  const drawPeerChart = (usersForChart) => {
    if (!svgRef2.current || usersForChart.length === 0) return;

    const peerAverages = calculatePeerAverages(usersForChart);
    drawSpiderChart(svgRef2.current, peerAverages, true);
  };

  // Calculate peer view averages (from all peer assessments for filtered users)
  const calculatePeerAverages = (users) => {
    if (users.length === 0) {
      return { spark: 0, anchor: 0, connector: 0, decider: 0, builder: 0, pulse: 0 };
    }

    // For each user, we have their peerScores (average of all peer assessments)
    // Now we average across all filtered users
    let peerAverages = {
      spark: 0,
      anchor: 0,
      connector: 0,
      decider: 0,
      builder: 0,
      pulse: 0
    };

    const usersWithPeerData = users.filter(user => user.peerScores && (
      user.peerScores.spark > 0 || user.peerScores.anchor > 0 || 
      user.peerScores.connector > 0 || user.peerScores.decider > 0 || 
      user.peerScores.builder > 0 || user.peerScores.pulse > 0
    ));

    if (usersWithPeerData.length === 0) {
      return peerAverages; // Return zeros if no peer data
    }

    peerAverages.spark = Math.round((usersWithPeerData.reduce((sum, u) => sum + (u.peerScores?.spark || 0), 0) / usersWithPeerData.length) * 10) / 10;
    peerAverages.anchor = Math.round((usersWithPeerData.reduce((sum, u) => sum + (u.peerScores?.anchor || 0), 0) / usersWithPeerData.length) * 10) / 10;
    peerAverages.connector = Math.round((usersWithPeerData.reduce((sum, u) => sum + (u.peerScores?.connector || 0), 0) / usersWithPeerData.length) * 10) / 10;
    peerAverages.decider = Math.round((usersWithPeerData.reduce((sum, u) => sum + (u.peerScores?.decider || 0), 0) / usersWithPeerData.length) * 10) / 10;
    peerAverages.builder = Math.round((usersWithPeerData.reduce((sum, u) => sum + (u.peerScores?.builder || 0), 0) / usersWithPeerData.length) * 10) / 10;
    peerAverages.pulse = Math.round((usersWithPeerData.reduce((sum, u) => sum + (u.peerScores?.pulse || 0), 0) / usersWithPeerData.length) * 10) / 10;

    return peerAverages;
  };

  // Generic spider chart drawing function
  const drawSpiderChart = (element, data, isPeer = false) => {
    d3.select(element).selectAll('*').remove();

    const width = 400;
    const height = 400;
    const margin = 60;
    const radius = Math.min(width, height) / 2 - margin;

    const svg = d3.select(element)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    const forces = [
      { key: 'spark', label: 'Spark', angle: 0, color: '#FF6B6B' },
      { key: 'anchor', label: 'Anchor', angle: Math.PI / 3, color: '#4ECDC4' },
      { key: 'connector', label: 'Connector', angle: 2 * Math.PI / 3, color: '#FFE66D' },
      { key: 'decider', label: 'Decider', angle: Math.PI, color: '#95E1D3' },
      { key: 'builder', label: 'Builder', angle: 4 * Math.PI / 3, color: '#F38181' },
      { key: 'pulse', label: 'Pulse', angle: 5 * Math.PI / 3, color: '#AA96DA' }
    ];

    // Draw concentric circles
    const levels = 5;
    for (let i = 1; i <= levels; i++) {
      svg.append('circle')
        .attr('r', (radius / levels) * i)
        .attr('fill', 'none')
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 1);
    }

    // Draw axes
    forces.forEach(force => {
      const x = radius * Math.cos(force.angle - Math.PI / 2);
      const y = radius * Math.sin(force.angle - Math.PI / 2);

      svg.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', x)
        .attr('y2', y)
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 1);

      const labelX = (radius + 30) * Math.cos(force.angle - Math.PI / 2);
      const labelY = (radius + 30) * Math.sin(force.angle - Math.PI / 2);

      svg.append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .attr('fill', '#1f2937')
        .text(force.label);
    });

    // Draw data polygon
    const points = forces.map(force => {
      const value = data[force.key] || 0;
      const angle = force.angle - Math.PI / 2;
      const r = (value / 100) * radius;
      return [r * Math.cos(angle), r * Math.sin(angle)];
    });

    const line = d3.line();
    svg.append('path')
      .attr('d', line(points.concat([points[0]])))
      .attr('fill', isPeer ? '#4ECDC4' : '#00D2C9')
      .attr('fill-opacity', 0.3)
      .attr('stroke', isPeer ? '#4ECDC4' : '#00D2C9')
      .attr('stroke-width', 2);

    // Draw points
    points.forEach((point, i) => {
      svg.append('circle')
        .attr('cx', point[0])
        .attr('cy', point[1])
        .attr('r', 4)
        .attr('fill', isPeer ? '#4ECDC4' : '#00D2C9');
    });
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted and paginated users
  const getSortedUsers = () => {
    let sorted = [...filteredUsers];

    sorted.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortConfig.direction === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    });

    return sorted;
  };

  const sortedUsers = getSortedUsers();
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Calculate averages for score columns (only users who completed assessment)
  const calculateAverages = () => {
    // Filter only users who completed the assessment
    const completedUsers = filteredUsers.filter(user => user.completedAt);
    
    if (completedUsers.length === 0) {
      return { spark: 0, anchor: 0, connector: 0, decider: 0, builder: 0, pulse: 0 };
    }
    
    const averages = {
      spark: 0,
      anchor: 0,
      connector: 0,
      decider: 0,
      builder: 0,
      pulse: 0
    };

    completedUsers.forEach(user => {
      averages.spark += user.spark || 0;
      averages.anchor += user.anchor || 0;
      averages.connector += user.connector || 0;
      averages.decider += user.decider || 0;
      averages.builder += user.builder || 0;
      averages.pulse += user.pulse || 0;
    });

    averages.spark = Math.round(averages.spark / completedUsers.length * 10) / 10;
    averages.anchor = Math.round(averages.anchor / completedUsers.length * 10) / 10;
    averages.connector = Math.round(averages.connector / completedUsers.length * 10) / 10;
    averages.decider = Math.round(averages.decider / completedUsers.length * 10) / 10;
    averages.builder = Math.round(averages.builder / completedUsers.length * 10) / 10;
    averages.pulse = Math.round(averages.pulse / completedUsers.length * 10) / 10;

    return averages;
  };

  // Calculate totals row
  const calculateTotals = () => {
    const totals = {
      spark: 0,
      anchor: 0,
      connector: 0,
      decider: 0,
      builder: 0,
      pulse: 0,
      invites: 0,
      responses: 0,
      shares: 0
    };

    filteredUsers.forEach(user => {
      totals.spark += user.spark || 0;
      totals.anchor += user.anchor || 0;
      totals.connector += user.connector || 0;
      totals.decider += user.decider || 0;
      totals.builder += user.builder || 0;
      totals.pulse += user.pulse || 0;
      totals.invites += user.invites || 0;
      totals.responses += user.responses || 0;
      totals.shares += user.shares || 0;
    });

    return totals;
  };

  const averages = calculateAverages();
  const totals = calculateTotals();

  // Handle CSV export
  const handleExport = () => {
    const headers = [
      'Name',
      'Email',
      'Spark',
      'Anchor',
      'Connector',
      'Decider',
      'Builder',
      'Pulse',
      '# of Invites',
      '# of Responses',
      '# of Shares'
    ];

    const rows = filteredUsers.map(user => [
      user.name,
      user.email,
      user.spark,
      user.anchor,
      user.connector,
      user.decider,
      user.builder,
      user.pulse,
      user.invites,
      user.responses,
      user.shares
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row =>
        row.map(cell =>
          typeof cell === 'string' && cell.includes(',')
            ? `"${cell}"`
            : cell
        ).join(',')
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `creative-footprint-dashboard-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleClose = () => {
    if (originRoute) {
      navigate(originRoute);
    } else {
      navigate('/welcome');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-banner">
          <button className="close-banner-btn" onClick={handleClose}>✕</button>
          <h1>Creative Work Footprint Dashboard</h1>
        </div>
        <div className="dashboard-content">
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-banner">
          <button className="close-banner-btn" onClick={handleClose}>✕</button>
          <h1>Creative Work Footprint Dashboard</h1>
        </div>
        <div className="dashboard-content">
          <div style={{ color: 'red', padding: '1rem' }}>Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Banner */}
      <div className="dashboard-banner">
        <button className="close-banner-btn" onClick={handleClose}>✕</button>
        <h1>Creative Work Footprint Dashboard</h1>
      </div>

      <div className="dashboard-content">
        {/* Filter Input */}
        <div className="filter-section">
          <label htmlFor="filter-input">Filter by name or email</label>
          <input
            id="filter-input"
            type="text"
            placeholder="Enter name or email..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="filter-input"
          />
          <p className="filter-info">
            Showing {filteredUsers.length} of {allUsers.length} users
          </p>
        </div>

        {/* Spider Charts */}
        {filteredUsers.length > 0 ? (
          <div className="charts-section">
            <div className="chart-container">
              <h3>Your Forces Score</h3>
              <svg ref={svgRef1}></svg>
            </div>
            <div className="chart-container">
              <h3>How others view</h3>
              <svg ref={svgRef2}></svg>
            </div>
          </div>
        ) : (
          <div className="empty-state">No users match your filter.</div>
        )}

        {/* Data Table */}
        {filteredUsers.length > 0 && (
          <div className="table-section">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')}>
                    Name {sortConfig.key === 'name' && (
                      sortConfig.direction === 'asc' ? '↑' : '↓'
                    )}
                  </th>
                  <th onClick={() => handleSort('email')}>
                    Email {sortConfig.key === 'email' && (
                      sortConfig.direction === 'asc' ? '↑' : '↓'
                    )}
                  </th>
                  <th onClick={() => handleSort('spark')}>
                    Spark {sortConfig.key === 'spark' && (
                      sortConfig.direction === 'asc' ? '↑' : '↓'
                    )}
                  </th>
                  <th onClick={() => handleSort('anchor')}>
                    Anchor {sortConfig.key === 'anchor' && (
                      sortConfig.direction === 'asc' ? '↑' : '↓'
                    )}
                  </th>
                  <th onClick={() => handleSort('connector')}>
                    Connector {sortConfig.key === 'connector' && (
                      sortConfig.direction === 'asc' ? '↑' : '↓'
                    )}
                  </th>
                  <th onClick={() => handleSort('decider')}>
                    Decider {sortConfig.key === 'decider' && (
                      sortConfig.direction === 'asc' ? '↑' : '↓'
                    )}
                  </th>
                  <th onClick={() => handleSort('builder')}>
                    Builder {sortConfig.key === 'builder' && (
                      sortConfig.direction === 'asc' ? '↑' : '↓'
                    )}
                  </th>
                  <th onClick={() => handleSort('pulse')}>
                    Pulse {sortConfig.key === 'pulse' && (
                      sortConfig.direction === 'asc' ? '↑' : '↓'
                    )}
                  </th>
                  <th onClick={() => handleSort('invites')}>
                    # Invites {sortConfig.key === 'invites' && (
                      sortConfig.direction === 'asc' ? '↑' : '↓'
                    )}
                  </th>
                  <th onClick={() => handleSort('responses')}>
                    # Responses {sortConfig.key === 'responses' && (
                      sortConfig.direction === 'asc' ? '↑' : '↓'
                    )}
                  </th>
                  <th onClick={() => handleSort('shares')}>
                    # Shares {sortConfig.key === 'shares' && (
                      sortConfig.direction === 'asc' ? '↑' : '↓'
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Averages Row - Pinned at top */}
                <tr className="averages-row">
                  <td className="averages-label">Average/Sums</td>
                  <td></td>
                  <td>{averages.spark}</td>
                  <td>{averages.anchor}</td>
                  <td>{averages.connector}</td>
                  <td>{averages.decider}</td>
                  <td>{averages.builder}</td>
                  <td>{averages.pulse}</td>
                  <td>{totals.invites}</td>
                  <td>{totals.responses}</td>
                  <td>{totals.shares}</td>
                </tr>

                {/* Data Rows */}
                {paginatedUsers.map((user, idx) => (
                  <tr key={`${user.id}-${idx}`}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.spark}</td>
                    <td>{user.anchor}</td>
                    <td>{user.connector}</td>
                    <td>{user.decider}</td>
                    <td>{user.builder}</td>
                    <td>{user.pulse}</td>
                    <td>{user.invites}</td>
                    <td>{user.responses}</td>
                    <td>{user.shares}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination & Export */}
            <div className="pagination-section">
              <div clasName="rows-per-page">
                <label htmlFor="rows-select">Rows per page</label>
                <select
                  id="rows-select"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="rows-select"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  ← Prev
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>

              <div className="export-section">
                <button className="btn-export" onClick={handleExport}>
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
